import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import DineInThunk from "../../../../services/thunks/DineInThunk";
import { friendlyDineInMessage } from "../../../../services/apiServices/DineInApiService";
import {
  DINE_IN_ROUND_STATUS,
  billableDineInItems,
  pendingDineInPlateCount,
  selectPlacedRounds,
} from "../../../../services/helpers/DineInSummaryHelper";
import { pluralize } from "../../../../services/helpers/PluralizeHelper";
import {
  clearSession,
  selectDineInBill,
  selectDineInLoadingMap,
  selectDineInRoundItems,
  selectDineInRounds,
  selectDineInSession,
} from "../../../../dataManagement/uiDb/DineInDb";
import ConfirmModal from "../../../shared/ConfirmModal/ConfirmModal";
import DineInBillSheet from "./DineInBillSheet";
import { buildBusinessPath } from "../../../../services/helpers/BusinessSlugHelper";
import { DineInRound } from "../../../../interfaces/DineInObject";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import "./styles/DineInActionBar.css";

interface DineInActionBarProps {
  businessId: string;
  businessName: string;
  openRound: DineInRound | null;
  openRoundItemCount: number;
}

/**
 * "View menu" routes to the business's existing product listing page — the
 * return trip (getting a menu selection back into an addItems call) is handled
 * via DineInOrderFab on the Products tab. The ?tab=products param selects that
 * tab and triggers BusinessListings' scroll-past-the-header effect.
 * "Order" closes the open round for real via submitRound; disabled with
 * nothing staged in it. "Generate bill" closes the table: enabled once there is
 * something billable and nothing still waiting on the kitchen (the server
 * refuses otherwise, 10020/10021), then confirmed, billed, and shown as a
 * read-only sheet while the stored table code is cleared so the next scan
 * starts a fresh table.
 */
const DineInActionBar: React.FC<DineInActionBarProps> = ({
  businessId,
  businessName,
  openRound,
  openRoundItemCount,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { triggerNotification } = useNotification();
  const loadingMap = useSelector(selectDineInLoadingMap);
  const isSubmitting = !!loadingMap.submitRound;
  const canOrder = !!openRound && openRoundItemCount > 0 && !isSubmitting;

  const session = useSelector(selectDineInSession);
  const rounds = useSelector(selectDineInRounds);
  const roundItems = useSelector(selectDineInRoundItems);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const bill = useSelector(selectDineInBill);
  const isGenerating = !!loadingMap.generateBill;

  const { hasBillable, waitingCount } = useMemo(() => {
    const placed = selectPlacedRounds(rounds, roundItems).items;
    return {
      hasBillable: billableDineInItems(placed).length > 0,
      waitingCount: pendingDineInPlateCount(placed),
    };
  }, [rounds, roundItems]);
  const canBill = hasBillable && waitingCount === 0 && !isGenerating && !bill;
  const billHint = !hasBillable
    ? "Order something to generate a bill."
    : waitingCount > 0
      ? `${pluralize(waitingCount, "item")} still waiting for the kitchen.`
      : "";

  const handleGenerateBill = async () => {
    if (!session || !canBill) return;
    setConfirmOpen(false);
    try {
      // The slice stores the bill and ends the session client-side on success.
      await dispatch(DineInThunk.generateBill({ sessionId: session.sessionId })).unwrap();
    } catch (error) {
      triggerNotification(
        // generateBill already rejects with friendly copy (same as the board).
        typeof error === "string" ? error : "Couldn't generate the bill. Please try again.",
        NotificationType.ERROR
      );
    }
  };

  // The slice already handles NOT_A_SESSION_MEMBER by kicking phase back to
  // "needsCode" — but that's a silent screen change with nothing else
  // explaining why. Toasting every failure here (mapped through the same
  // friendlyDineInMessage lookup the slice uses) covers that case too,
  // without special-casing it separately.
  const handleOrder = async () => {
    if (!openRound || !canOrder) return;
    try {
      // REQUESTED, not CLOSED: this is the table handing the round to the
      // kitchen, and REQUESTED is what the kitchen's own view keys off. The
      // literal used to be "CLOSED", which isn't part of the round vocabulary
      // at all.
      await dispatch(
        DineInThunk.submitRound({
          roundId: openRound.roundId,
          status: DINE_IN_ROUND_STATUS.REQUESTED,
        })
      ).unwrap();
    } catch (error) {
      const message =
        typeof error === "string"
          ? friendlyDineInMessage(error)
          : "Couldn't send your order. Please try again.";
      triggerNotification(message, NotificationType.ERROR);
    }
  };

  return (
    <div className="dine-in-action-bar">
      <button
        className="dine-in-action-bar__btn"
        onClick={() => {
          const validName =
            businessName && businessName.trim().toLowerCase() !== "this table"
              ? businessName
              : undefined;
          navigate(`${buildBusinessPath(businessId, validName)}?tab=products`);
        }}
      >
        View menu
      </button>
      <button
        className="dine-in-action-bar__btn dine-in-action-bar__btn--primary"
        onClick={handleOrder}
        disabled={!canOrder}
      >
        {isSubmitting ? "Ordering..." : "Order"}
      </button>
      <button
        className="dine-in-action-bar__btn"
        onClick={() => setConfirmOpen(true)}
        disabled={!canBill}
        title={billHint || undefined}
      >
        {isGenerating ? "Billing..." : "Generate bill"}
      </button>
      {billHint && <p className="dine-in-action-bar__hint">{billHint}</p>}

      <ConfirmModal
        isOpen={confirmOpen}
        title="Generate the bill?"
        message="This closes the table. No more items can be added after this."
        confirmLabel="Generate bill"
        isLoading={isGenerating}
        onConfirm={handleGenerateBill}
        onCancel={() => setConfirmOpen(false)}
      />

      {bill && session && (
        <DineInBillSheet
          order={bill}
          tableNumber={session.tableNumber}
          businessName={businessName}
          onClose={() => {
            dispatch(clearSession());
            const validName =
              businessName && businessName.trim().toLowerCase() !== "this table"
                ? businessName
                : undefined;
            navigate(buildBusinessPath(businessId, validName));
          }}
        />
      )}
    </div>
  );
};

export default DineInActionBar;
